export const PLATFORMS = ["TikTok", "Instagram", "Facebook", "YouTube"];

export const EVIDENCE_NOTE =
  "ScamLizard reports may use public marketing materials, landing pages, screenshots, user-submitted conversations, payment requests, and other available evidence. Not every review involves direct contact with the seller.";

export const VERDICT_FOOTNOTE =
  "ScamLizard reports are informational and are not legal findings of fraud.";

export const scams = [
  {
    rank: 1,
    slug: "cash-flip-social-media-offer",
    name: "Cash-Flip Social Media Offer",
    desc: "A social-media money offer promising a small payment can unlock a much larger return.",
    platforms: ["TikTok", "Direct Messages"],
    score: 9.7,
    label: "VERY HIGH",
    confidence: "HIGH",
    flags: ["Payment Before Result", "Off-Platform Funnel", "Pressure Follow-Ups"],
    type: "case",
    hasReport: true,
    quip: "MM! MMMM!",
  },
  {
    rank: 2,
    slug: "creator-income-starter-guide",
    name: "Creator Income Starter Guide — $1.28",
    desc: "A very low-cost digital guide pitched to creators through a faith-forward social profile.",
    platforms: ["TikTok", "Link in Bio"],
    score: 4.6,
    label: "USE CAUTION",
    confidence: "MODERATE TO HIGH",
    flags: ["Low-Price Hook", "Broad Positioning"],
    type: "case",
    hasReport: true,
  },
  {
    rank: 3,
    slug: "autoincome-ai",
    name: "AutoIncome AI",
    desc: "AI builds an automated online income business while you sleep.",
    platforms: ["TikTok", "YouTube"],
    score: 9.6,
    label: "EXTREME",
    confidence: "HIGH",
    flags: ["Hidden Upsells", "Fake Scarcity", "Immortal Deadline"],
    type: "demo",
    hasReport: true,
  },
  {
    rank: 4,
    slug: "millionaire-machine",
    name: "Millionaire Machine",
    desc: "A secret automated system supposedly generates daily cash.",
    platforms: ["Instagram", "Facebook"],
    score: 9.4,
    label: "EXTREME",
    confidence: "HIGH",
    flags: ["Earnings Claims", "Unverified Testimonials", "Stretchy Timeline"],
    type: "demo",
    hasReport: true,
  },
  {
    rank: 5,
    slug: "dreamgrowth-blueprint",
    name: "DreamGrowth Blueprint",
    desc: "A free AI business system promoted through a long social-media sales video.",
    platforms: ["TikTok", "Instagram", "Facebook"],
    score: 9.2,
    label: "EXTREME",
    confidence: "HIGH",
    flags: ["Stretchy Timeline", "Artificial Scarcity", "Hidden Cost"],
    type: "demo",
    hasReport: true,
  },
  {
    rank: 6,
    slug: "robot-revenue-club",
    name: "Robot Revenue Club",
    desc: "Let the robots create passive income for you.",
    platforms: ["TikTok", "YouTube"],
    score: 8.9,
    label: "HIGH",
    confidence: "MEDIUM",
    flags: ["Earnings Claims", "Pressure Selling"],
    type: "soon",
  },
  {
    rank: 7,
    slug: "passive-profit-portal",
    name: "Passive Profit Portal",
    desc: "A members-only portal promising hands-free monthly profits.",
    platforms: ["Facebook", "Instagram"],
    score: 8.7,
    label: "HIGH",
    confidence: "HIGH",
    flags: ["Hidden Cost", "Immortal Deadline"],
    type: "soon",
  },
  {
    rank: 8,
    slug: "ai-store-fortune",
    name: "AI Store Fortune",
    desc: "Pre-built AI online stores sold as instant income machines.",
    platforms: ["Instagram", "TikTok"],
    score: 8.4,
    label: "HIGH",
    confidence: "MEDIUM",
    flags: ["Fake Scarcity", "Unverified Testimonials"],
    type: "soon",
  },
  {
    rank: 9,
    slug: "freedom-funnel-system",
    name: "Freedom Funnel System",
    desc: "A done-for-you funnel that supposedly prints money on autopilot.",
    platforms: ["Facebook", "YouTube"],
    score: 8.1,
    label: "HIGH",
    confidence: "MEDIUM",
    flags: ["Hidden Upsells", "Stretchy Timeline"],
    type: "soon",
  },
  {
    rank: 10,
    slug: "cashpilot-ai",
    name: "CashPilot AI",
    desc: "An AI copilot that claims to earn for you daily while you watch.",
    platforms: ["YouTube", "TikTok"],
    score: 7.8,
    label: "HIGH",
    confidence: "MEDIUM",
    flags: ["Earnings Claims", "Pressure Selling"],
    type: "soon",
  },
];

const demoReceipts = [
  {
    id: "landing-page-review",
    title: "Landing Page Review",
    body: "Our reviewers examined the offer page across multiple visits and devices, documenting the claims presented, the urgency messaging shown, and how costs were introduced relative to the initial pitch.",
  },
  {
    id: "sales-presentation-analysis",
    title: "Sales Presentation Analysis",
    body: "The full sales presentation was reviewed end to end, noting persuasion patterns, earnings framing, and where key commitments were first disclosed to the viewer.",
  },
  {
    id: "repeated-visit-test",
    title: "Repeated Visit Test",
    body: "The offer was revisited over several days from fresh sessions to observe whether countdowns, deadlines and scarcity claims behaved consistently or reset.",
  },
  {
    id: "consumer-complaint-pattern",
    title: "Consumer Complaint Pattern",
    body: "Publicly posted consumer discussions about similar offers were reviewed to identify recurring themes around unexpected costs, refund difficulty, and post-purchase experience.",
  },
  {
    id: "business-identity-research",
    title: "Domain / Business Identity Research",
    body: "Basic registration and identity signals behind the offer were examined to understand who operates it, how long it has existed, and whether contact details are transparent.",
  },
].map((r) => ({ ...r, tag: "Demonstration Evidence" }));

export const reports = {
  "cash-flip-social-media-offer": {
    rank: 1,
    name: "Cash-Flip Social Media Offer",
    caseType: "real",
    checkedDates: "Sep 18–21, 2026",
    score: 9.7,
    riskKey: "VERY HIGH",
    riskLabel: "VERY HIGH RISK",
    confidence: "HIGH",
    evidenceSource: "Mixed sources — public marketing + user-submitted conversation",
    lastReviewed: "September 18–21, 2026",
    pillars: [
      {
        id: "dream",
        title: "THE DREAM",
        body: "A social-media money opportunity suggests that a small payment can unlock a much larger return.",
      },
      {
        id: "goal",
        title: "THE GOAL",
        body: "Move the target off the platform and get them to send money using crypto or other direct-payment methods.",
      },
      {
        id: "catch",
        title: "THE CATCH",
        body: "The offer relies on wealth imagery, payment instructions, and follow-up pressure rather than verifiable business details, buyer protections, or a clear product/service exchange.",
      },
    ],
    findingsSub: "Eight warning signals documented in the reviewed material.",
    findings: [
      { id: "wealth-imagery", title: "Wealth Imagery as Social Proof", body: "Large-money imagery used as social proof." },
      { id: "learn-the-play", title: "“Learn the Play” Framing", body: "“Opportunity” / “learn the play” style framing." },
      { id: "payment-first", title: "Payment Before Any Result", body: "Instructions to send payment before any result." },
      { id: "screenshot-request", title: "Screenshot After Payment", body: "Request for a screenshot after payment." },
      { id: "follow-up-pressure", title: "Follow-Up Pressure", body: "Follow-up pressure such as “sent?” and “ok..?”." },
      { id: "payment-pivoting", title: "Payment-Method Pivoting", body: "Payment-method pivoting when one option is unavailable." },
      { id: "crypto-p2p", title: "Crypto & Peer-to-Peer Payments", body: "Use of crypto and peer-to-peer payment methods." },
      { id: "off-platform", title: "Off-Platform Funnel", body: "Off-platform messaging / funnel behavior." },
    ],
    trustTactics: [
      { name: "Extraordinary Return Claim", status: "Detected" },
      { name: "Urgency / Pressure", status: "Detected" },
      { name: "Peer-to-Peer Payment Request", status: "Detected" },
      { name: "Social Proof / Wealth Signaling", status: "High" },
      { name: "Off-Platform Funnel", status: "Detected" },
      { name: "Faith-Based Trust Appeal", status: "Not observed" },
    ],
    riskMechanics: [
      { name: "Refund / Recovery Difficulty", status: "High" },
      { name: "Payment Protection", status: "Low" },
      { name: "Identity / Data Collection Risk", status: "Moderate" },
      { name: "Upsell Exposure", status: "Unknown" },
      { name: "Recurring Billing Risk", status: "Not observed" },
    ],
    platformInfo: [
      { label: "Observed on", value: "Social platform + direct messaging" },
      { label: "Payment methods referenced", value: "Bitcoin / Cash App / Zelle / PayPal" },
    ],
    verdictTitle: "ScamLizard Conclusion",
    verdictBody:
      "ScamLizard found multiple high-risk signals in the material reviewed. This case study documents observed offer behavior and payment-request patterns. It does not independently prove that a specific person committed fraud, but the reviewed materials showed enough warning signs that ScamLizard would advise extreme caution.",
    receipts: [
      {
        id: "case1-receipt-1",
        title: "Profile Context",
        image: "/receipts/case1-profile.png",
        caption: "Profile context showing money/luxury branding and link-funnel setup",
        tag: "Case Receipt",
      },
      {
        id: "case1-receipt-2",
        title: "Promotional Wealth Imagery",
        image: "/receipts/case1-promo.png",
        caption: "Promotional wealth imagery used to market the opportunity",
        tag: "Case Receipt",
      },
      {
        id: "case1-receipt-3",
        title: "Payment Conversation",
        image: "/receipts/case1-chat.png",
        caption: "Reconstructed from the original exchange; identifying details removed",
        tag: "Case Receipt • Reconstructed",
      },
    ],
  },

  "creator-income-starter-guide": {
    rank: 2,
    name: "Creator Income Starter Guide — $1.28",
    caseType: "real",
    checkedDates: "Sep 18–21, 2026",
    score: 4.6,
    riskKey: "USE CAUTION",
    riskLabel: "USE CAUTION",
    confidence: "MODERATE TO HIGH",
    evidenceSource: "Public materials",
    lastReviewed: "September 18–21, 2026",
    pillars: [
      {
        id: "dream",
        title: "THE DREAM",
        body: "A low-cost starter guide presents itself as a quick way for creators to stop overthinking, choose a practical offer, and make a first move.",
      },
      {
        id: "goal",
        title: "THE GOAL",
        body: "Convert curious social-media viewers into a low-friction purchase through a very inexpensive entry offer.",
      },
      {
        id: "catch",
        title: "THE CATCH",
        body: "The price is extremely low and the presentation is clean, but the offer remains broad. A buyer may still have limited clarity about the actual depth, format, and practical value of the guide before purchase.",
      },
    ],
    findingsSub: "What we observed in the public materials.",
    findings: [
      { id: "entry-price", title: "Extremely Low Entry Price", body: "The full offer is priced at $1.28 one-time." },
      { id: "clean-presentation", title: "Clean, Simple Presentation", body: "The offer page is tidy, direct, and easy to understand at a glance." },
      { id: "digital-guide", title: "Digital Guide Framing", body: "The product is described as a digital guide." },
      { id: "no-recurring", title: "No Recurring Charges Observed", body: "No subscription or recurring-charge language observed." },
      { id: "earnings-disclaimer", title: "Earnings Disclaimer Present", body: "The page states that earnings are not guaranteed." },
      { id: "broad-offer", title: "Broad Positioning", body: "The offer description remains broad/lightweight." },
      { id: "limited-specificity", title: "Limited Specificity", body: "Limited specificity about the full depth or uniqueness of the guide." },
    ],
    trustTactics: [
      { name: "Low-Price Curiosity Hook", status: "Detected" },
      { name: "Authority / Credibility Appeal", status: "Moderate" },
      { name: "Faith-Based Trust Appeal", status: "Detected" },
      { name: "Urgency / Pressure", status: "Not observed" },
      { name: "Extraordinary Earnings Claim", status: "Not observed" },
      { name: "Social Proof", status: "Moderate" },
    ],
    riskMechanics: [
      { name: "Low-Dollar Dispute Friction", status: "Elevated" },
      { name: "Refund / Recovery Difficulty", status: "Unknown" },
      { name: "Payment Protection", status: "Standard card checkout observed" },
      { name: "Recurring Billing Risk", status: "Low / Not observed" },
      { name: "Upsell Exposure", status: "Unknown" },
      { name: "Identity / Data Collection Risk", status: "Low to Moderate" },
    ],
    platformInfo: [
      { label: "Observed on", value: "Social profile + linked offer page" },
      { label: "Offer type", value: "Low-cost digital guide" },
      { label: "Price observed", value: "$1.28 one-time" },
    ],
    verdictTitle: "ScamLizard Conclusion",
    verdictBody:
      "ScamLizard did not find the same kind of severe warning signals seen in high-pressure payment schemes. However, the offer relies on a very low price and broad positioning, which can encourage curiosity purchases before a buyer fully understands the value of what they are receiving. Low price does not prove wrongdoing, but low price alone is also not proof of fraud. ScamLizard would classify this offer as Use Caution based on the materials reviewed.",
    receipts: [
      {
        id: "case2-receipt-1",
        title: "Public Profile",
        image: "/receipts/case2-profile.png",
        caption: "Public profile promoting the linked offer",
        tag: "Case Receipt",
      },
      {
        id: "case2-receipt-2",
        title: "Offer Page",
        image: "/receipts/case2-offer.png",
        caption: "Offer page showing price, product framing, and stated limitations",
        tag: "Case Receipt",
      },
    ],
  },

  "autoincome-ai": {
    rank: 3,
    name: "AutoIncome AI",
    caseType: "demo",
    score: 9.6,
    riskKey: "EXTREME",
    riskLabel: "EXTREME RISK",
    confidence: "HIGH",
    evidenceSource: "Demonstration materials (fictional)",
    lastReviewed: "September 2026",
    platforms: ["TikTok", "YouTube"],
    pillars: [
      {
        id: "dream",
        title: "THE DREAM",
        body: "AI builds and runs an automated online income business for you while you sleep — no skills, no effort, no risk.",
      },
      {
        id: "goal",
        title: "THE GOAL",
        body: "Move viewers through a polished sales funnel into an entry purchase, then a chain of progressively expensive upsells.",
      },
      {
        id: "catch",
        title: "THE CATCH",
        body: "The 'automated income' depends on buying into the system itself, and the real costs and required effort only appear after the first payment.",
      },
    ],
    findingsSub: "Three warning signals documented during review.",
    findings: [
      {
        id: "hidden-upsells",
        title: "HIDDEN UPSELLS",
        status: "OBSERVED",
        body: "The advertised entry price is only the first step; additional paid tiers are revealed one at a time after checkout.",
      },
      {
        id: "fake-scarcity",
        title: "FAKE SCARCITY",
        status: "REPRODUCED",
        body: "Limited-spot messaging appears on every visit, regardless of when or how often the page is opened.",
      },
      {
        id: "immortal-deadline",
        title: "IMMORTAL DEADLINE",
        status: "REPRODUCED",
        body: "The 'final chance' countdown resets on repeat visits and never actually expires.",
      },
    ],
    trustTactics: [
      { name: "Extraordinary Return Claim", status: "Detected" },
      { name: "Urgency / Pressure", status: "Detected" },
      { name: "Scarcity", status: "Detected" },
      { name: "Social Proof / Wealth Signaling", status: "Moderate" },
    ],
    riskMechanics: [
      { name: "Upsell Exposure", status: "High" },
      { name: "Recurring Billing Risk", status: "Unknown" },
      { name: "Refund / Recovery Difficulty", status: "Unknown" },
    ],
    verdictTitle: "Why ScamLizard rated it 9.6",
    verdictBody:
      "ScamLizard Risk Scores are based on documented warning signals, corroborating evidence, transparency of costs and claims, complaint patterns, pressure tactics and reproducible presentation behaviour.",
    verdictFootnote: "A high ScamLizard Risk Score is a consumer risk assessment, not a legal finding of fraud.",
    receipts: demoReceipts,
  },

  "millionaire-machine": {
    rank: 4,
    name: "Millionaire Machine",
    caseType: "demo",
    score: 9.4,
    riskKey: "EXTREME",
    riskLabel: "EXTREME RISK",
    confidence: "HIGH",
    evidenceSource: "Demonstration materials (fictional)",
    lastReviewed: "September 2026",
    platforms: ["Instagram", "Facebook"],
    pillars: [
      {
        id: "dream",
        title: "THE DREAM",
        body: "A secret automated system supposedly generates daily cash for ordinary people with no experience.",
      },
      {
        id: "goal",
        title: "THE GOAL",
        body: "Keep viewers watching a long, emotionally charged presentation and convert them into buyers before they pause to research.",
      },
      {
        id: "catch",
        title: "THE CATCH",
        body: "The earnings claims and testimonials could not be verified, and the timeline from purchase to profit stretches far beyond what the ads suggest.",
      },
    ],
    findingsSub: "Three warning signals documented during review.",
    findings: [
      {
        id: "earnings-claims",
        title: "EARNINGS CLAIMS",
        status: "OBSERVED",
        body: "Specific daily income figures are presented as typical results without supporting evidence.",
      },
      {
        id: "unverified-testimonials",
        title: "UNVERIFIED TESTIMONIALS",
        status: "UNVERIFIED",
        body: "Strong income testimonials are presented without independent evidence confirming the claimed results.",
      },
      {
        id: "stretchy-timeline",
        title: "STRETCHY TIMELINE",
        status: "REPRODUCED",
        body: "The presentation appears far shorter than its actual duration, keeping viewers invested longer than intended.",
      },
    ],
    trustTactics: [
      { name: "Extraordinary Return Claim", status: "Detected" },
      { name: "Social Proof / Wealth Signaling", status: "High" },
      { name: "Urgency / Pressure", status: "Moderate" },
    ],
    riskMechanics: [
      { name: "Refund / Recovery Difficulty", status: "Unknown" },
      { name: "Upsell Exposure", status: "Moderate" },
      { name: "Recurring Billing Risk", status: "Unknown" },
    ],
    verdictTitle: "Why ScamLizard rated it 9.4",
    verdictBody:
      "ScamLizard Risk Scores are based on documented warning signals, corroborating evidence, transparency of costs and claims, complaint patterns, pressure tactics and reproducible presentation behaviour.",
    verdictFootnote: "A high ScamLizard Risk Score is a consumer risk assessment, not a legal finding of fraud.",
    receipts: demoReceipts,
  },

  "dreamgrowth-blueprint": {
    rank: 5,
    name: "DreamGrowth Blueprint",
    caseType: "demo",
    score: 9.2,
    riskKey: "EXTREME",
    riskLabel: "EXTREME RISK",
    confidence: "HIGH",
    evidenceSource: "Demonstration materials (fictional)",
    lastReviewed: "September 2026",
    platforms: ["TikTok", "Instagram", "Facebook"],
    pillars: [
      {
        id: "dream",
        title: "THE DREAM",
        body: "AI creates and operates an online business for you, producing substantial passive income with little effort.",
      },
      {
        id: "goal",
        title: "THE GOAL",
        body: "Keep the viewer emotionally invested in a persuasive sales presentation and move them toward enrollment.",
      },
      {
        id: "catch",
        title: "THE CATCH",
        body: "The 'free' opportunity leads into a sales process where the true commitment and potential additional costs are not obvious at the beginning.",
      },
    ],
    findingsSub: "Six warning signals documented during review.",
    findings: [
      {
        id: "stretchy-timeline",
        title: "STRETCHY TIMELINE",
        status: "REPRODUCED",
        body: "The video progress display makes the presentation appear much shorter than its actual duration.",
        stats: [
          { label: "Actual presentation", value: "21:47" },
          { label: "Initial visual impression", value: "Approx. 2–3 minutes" },
        ],
      },
      {
        id: "immortal-deadline",
        title: "IMMORTAL DEADLINE",
        status: "REPRODUCED",
        body: "Urgency messaging continues to appear on repeat visits despite suggesting the opportunity is about to disappear.",
      },
      {
        id: "artificial-scarcity",
        title: "ARTIFICIAL SCARCITY",
        status: "OBSERVED",
        body: "Claims such as 'only 10 spots available' are used to pressure an immediate decision.",
      },
      {
        id: "miracle-crowd",
        title: "MIRACLE CROWD",
        status: "UNVERIFIED",
        body: "'People watching right now' counters create social proof, but the displayed audience could not be independently verified.",
      },
      {
        id: "testimonial-authenticity",
        title: "TESTIMONIAL AUTHENTICITY",
        status: "UNVERIFIED",
        body: "Strong income testimonials are presented without independent evidence confirming the claimed results.",
      },
      {
        id: "true-cost",
        title: "TRUE COST",
        status: "UNCLEAR",
        body: "The presentation emphasizes free or low-risk entry while the complete financial commitment is not clearly presented upfront.",
      },
    ],
    trustTactics: [
      { name: "Urgency / Pressure", status: "Detected" },
      { name: "Scarcity", status: "Detected" },
      { name: "Social Proof / Wealth Signaling", status: "Moderate" },
      { name: "Extraordinary Return Claim", status: "Moderate" },
    ],
    riskMechanics: [
      { name: "Upsell Exposure", status: "High" },
      { name: "Recurring Billing Risk", status: "Unknown" },
      { name: "Refund / Recovery Difficulty", status: "Unknown" },
    ],
    verdictTitle: "Why ScamLizard rated it 9.2",
    verdictBody:
      "ScamLizard Risk Scores are based on documented warning signals, corroborating evidence, transparency of costs and claims, complaint patterns, pressure tactics and reproducible presentation behaviour.",
    verdictFootnote: "A high ScamLizard Risk Score is a consumer risk assessment, not a legal finding of fraud.",
    receipts: demoReceipts,
  },
};
